import React from 'react';
import { TimelineMode } from '@models/TimelineModel';
import TimelineHorizontal from '../timeline-horizontal/timeline-horizontal';
import TimelineVertical from '../timeline-vertical/timeline-vertical';
import { Scroll } from '@models/TimelineHorizontalModel';
import { main as timelineMain } from './timeline-new.css';
import { pickDefined } from '../../utils/propUtils';

interface TimelineViewProps {
  timelineMode: string;
  activeTimelineItem: number;
  autoScroll: (scroll: Partial<Scroll>) => void;
  contentDetailsChildren?: React.ReactNode;
  hasFocus: boolean;
  iconChildren?: React.ReactNode;
  items: any[]; // Using any to avoid type conflicts
  handleTimelineItemClick: (itemId?: string) => void;
  handleTimelineItemElapsed: (itemId?: string) => void;
  slideShowRunning?: boolean;
  id: string;
  theme?: any;
  lineWidth?: number;
  onOutlineSelection?: (index: number) => void;
  nestedCardHeight: number;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  timelineMode,
  activeTimelineItem,
  autoScroll,
  contentDetailsChildren,
  hasFocus,
  iconChildren,
  items,
  handleTimelineItemClick,
  handleTimelineItemElapsed,
  slideShowRunning,
  id,
  theme,
  lineWidth,
  onOutlineSelection,
  nestedCardHeight,
}) => {
  // Horizontal Timeline (regular or "all cards" mode)
  if (timelineMode === 'HORIZONTAL' || timelineMode === 'HORIZONTAL_ALL') {
    return (
      <TimelineHorizontal
        autoScroll={autoScroll}
        contentDetailsChildren={contentDetailsChildren}
        handleItemClick={handleTimelineItemClick}
        hasFocus={hasFocus}
        iconChildren={iconChildren}
        items={items}
        mode={timelineMode as TimelineMode}
        onElapsed={handleTimelineItemElapsed}
        wrapperId={id}
        nestedCardHeight={nestedCardHeight}
        theme={theme}
        {...(lineWidth !== undefined && { lineWidth })}
        {...pickDefined({
          slideShowRunning,
        })}
      />
    );
  }

  // Vertical Alternating Timeline
  if (timelineMode === 'VERTICAL_ALTERNATING') {
    return (
      <TimelineVertical
        activeTimelineItem={activeTimelineItem}
        alternateCards={true}
        autoScroll={autoScroll}
        contentDetailsChildren={contentDetailsChildren}
        hasFocus={hasFocus}
        iconChildren={iconChildren}
        items={items}
        mode={timelineMode as TimelineMode}
        onClick={handleTimelineItemClick}
        onElapsed={handleTimelineItemElapsed}
        theme={theme}
        nestedCardHeight={nestedCardHeight}
        {...pickDefined({
          slideShowRunning,
        })}
      />
    );
  }

  // Vertical Timeline (default)
  return (
    <TimelineVertical
      activeTimelineItem={activeTimelineItem}
      alternateCards={false}
      autoScroll={autoScroll}
      contentDetailsChildren={contentDetailsChildren}
      hasFocus={hasFocus}
      iconChildren={iconChildren}
      items={items}
      mode={timelineMode as TimelineMode}
      onClick={handleTimelineItemClick}
      onElapsed={handleTimelineItemElapsed}
      theme={theme}
      nestedCardHeight={nestedCardHeight}
      {...pickDefined({
        slideShowRunning,
        onOutlineSelection,
      })}
    />
  );
};

export default React.memo(TimelineView);
